import * as THREE from 'three';

import { useFrame } from '@react-three/fiber';
import { useRef } from 'react';

type LibraryBuildingProps = {
  position: [number, number, number];
  scale?: number;
  rotation?: [number, number, number];
};

export function LibraryBuilding({
  position = [0, 0, 0],
  scale = 1,
  rotation = [0, 0, 0],
}: LibraryBuildingProps) {
  const buildingColor = '#E8EAED'; // Light gray for the building
  const roofColor = '#10A37F'; // OpenAI green
  const doorColor = '#4A2511'; // Dark brown for the door
  const windowColor = '#B3E5FC'; // Light blue for windows
  const logoColor = '#10A37F'; // OpenAI green for the logo

  const doorRef = useRef<THREE.Mesh>(null);
  const logoRef = useRef<THREE.Group>(null);

  // Animate the logo to gently rotate
  useFrame((state) => {
    if (logoRef.current) {
      logoRef.current.rotation.y =
        Math.sin(state.clock.getElapsedTime() * 0.5) * 0.1;
    }
  });

  return (
    <group position={position} scale={scale} rotation={rotation}>
      {/* Main building structure - larger than standard buildings */}
      <mesh position={[0, 3, 0]} castShadow receiveShadow>
        <boxGeometry args={[8, 6, 8]} />
        <meshLambertMaterial color={buildingColor} flatShading />
      </mesh>

      {/* Roof - flat roof with slight angle for a modern library look */}
      <mesh position={[0, 6.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[8.5, 1, 8.5]} />
        <meshLambertMaterial color={roofColor} flatShading />
      </mesh>

      {/* Steps leading to the entrance */}
      <mesh position={[0, 0.2, 4.5]} castShadow receiveShadow>
        <boxGeometry args={[4, 0.4, 1.5]} />
        <meshLambertMaterial color="#B0BEC5" flatShading />
      </mesh>

      {/* Front columns */}
      <mesh position={[-2, 1.75, 4]} castShadow receiveShadow>
        <boxGeometry args={[0.6, 3.5, 0.6]} />
        <meshLambertMaterial color={buildingColor} flatShading />
      </mesh>
      <mesh position={[2, 1.75, 4]} castShadow receiveShadow>
        <boxGeometry args={[0.6, 3.5, 0.6]} />
        <meshLambertMaterial color={buildingColor} flatShading />
      </mesh>

      {/* Big Door */}
      <mesh ref={doorRef} position={[0, 1.5, 4.01]} castShadow receiveShadow>
        <boxGeometry args={[2.5, 3, 0.2]} />
        <meshLambertMaterial color={doorColor} flatShading />
      </mesh>

      {/* Door handle */}
      <mesh position={[0.8, 1.5, 4.12]} castShadow>
        <boxGeometry args={[0.2, 0.2, 0.1]} />
        <meshLambertMaterial color="#FFCA28" flatShading />
      </mesh>

      {/* Windows - front */}
      <mesh position={[-2.5, 3, 4.01]} castShadow receiveShadow>
        <boxGeometry args={[1.5, 1.5, 0.1]} />
        <meshLambertMaterial color={windowColor} flatShading />
      </mesh>
      <mesh position={[2.5, 3, 4.01]} castShadow receiveShadow>
        <boxGeometry args={[1.5, 1.5, 0.1]} />
        <meshLambertMaterial color={windowColor} flatShading />
      </mesh>

      {/* Windows - sides */}
      {/* Left side */}
      <mesh position={[-4.01, 2, 2]} castShadow receiveShadow>
        <boxGeometry args={[0.1, 1.5, 1.5]} />
        <meshLambertMaterial color={windowColor} flatShading />
      </mesh>
      <mesh position={[-4.01, 2, -2]} castShadow receiveShadow>
        <boxGeometry args={[0.1, 1.5, 1.5]} />
        <meshLambertMaterial color={windowColor} flatShading />
      </mesh>
      <mesh position={[-4.01, 4, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.1, 1.5, 3]} />
        <meshLambertMaterial color={windowColor} flatShading />
      </mesh>

      {/* Right side */}
      <mesh position={[4.01, 2, 2]} castShadow receiveShadow>
        <boxGeometry args={[0.1, 1.5, 1.5]} />
        <meshLambertMaterial color={windowColor} flatShading />
      </mesh>
      <mesh position={[4.01, 2, -2]} castShadow receiveShadow>
        <boxGeometry args={[0.1, 1.5, 1.5]} />
        <meshLambertMaterial color={windowColor} flatShading />
      </mesh>
      <mesh position={[4.01, 4, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.1, 1.5, 3]} />
        <meshLambertMaterial color={windowColor} flatShading />
      </mesh>

      {/* Back windows */}
      <mesh position={[0, 3, -4.01]} castShadow receiveShadow>
        <boxGeometry args={[4, 2, 0.1]} />
        <meshLambertMaterial color={windowColor} flatShading />
      </mesh>

      {/* Library sign above the door */}
      <mesh position={[0, 5, 4.05]} castShadow receiveShadow>
        <boxGeometry args={[6, 0.8, 0.2]} />
        <meshLambertMaterial color="#EEEEEE" flatShading />
      </mesh>

      {/* OpenAI logo on the front - simplified as geometric shapes */}
      <group ref={logoRef} position={[0, 5, 4.2]}>
        {/* Create a simplified OpenAI logo using boxes */}
        <mesh position={[-1.5, 0, 0]} castShadow>
          <boxGeometry args={[0.4, 0.4, 0.1]} />
          <meshLambertMaterial color={logoColor} flatShading />
        </mesh>
        <mesh position={[-0.9, 0, 0]} castShadow>
          <boxGeometry args={[0.4, 0.4, 0.1]} />
          <meshLambertMaterial color={logoColor} flatShading />
        </mesh>
        <mesh position={[-0.3, 0, 0]} castShadow>
          <boxGeometry args={[0.4, 0.4, 0.1]} />
          <meshLambertMaterial color={logoColor} flatShading />
        </mesh>
        <mesh position={[0.3, 0, 0]} castShadow>
          <boxGeometry args={[0.4, 0.4, 0.1]} />
          <meshLambertMaterial color={logoColor} flatShading />
        </mesh>
        <mesh position={[0.9, 0, 0]} castShadow>
          <boxGeometry args={[0.4, 0.4, 0.1]} />
          <meshLambertMaterial color={logoColor} flatShading />
        </mesh>
        <mesh position={[1.5, 0, 0]} castShadow>
          <boxGeometry args={[0.4, 0.4, 0.1]} />
          <meshLambertMaterial color={logoColor} flatShading />
        </mesh>
      </group>

      {/* Bookshelves visible through windows */}
      <mesh position={[-2, 1.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[1, 3, 5]} />
        <meshLambertMaterial color="#8D6E63" flatShading />
      </mesh>
      <mesh position={[2, 1.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[1, 3, 5]} />
        <meshLambertMaterial color="#8D6E63" flatShading />
      </mesh>
      <mesh position={[0, 1.5, -2]} castShadow receiveShadow>
        <boxGeometry args={[3, 3, 1]} />
        <meshLambertMaterial color="#8D6E63" flatShading />
      </mesh>
    </group>
  );
}
